import { useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import type { Itinerary } from "@b2bportal/ground-api";
import type { GroundCancelScenarioV2 } from "@b2bportal/rbc-ground-cancel-v2-api";
import { GroundCancelScenarioV2Enum } from "@b2bportal/rbc-ground-cancel-v2-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Button, Card } from "@tenant/ui";
import DOMPurify from "dompurify";
import defaultStyles from "./CarTripCancellationPolicy.module.scss";
import { getCancelCopy } from "./getCancelCopy";
import { useCarCancelPolicyBreakdown } from "@/modules/cars-postbooking/hooks";
import {
  CancelImportantInfo,
  RefundBreakdown,
} from "@/pages/Trips/shared/components";

export { getCancelCopy } from "./getCancelCopy";

export interface CarTripCancellationPolicyProps {
  itinerary: Itinerary;
  cancelScenario: GroundCancelScenarioV2;
  onCancel: () => void;
}

const ComponentName = "CarTripCancellationPolicy";

export const CarTripCancellationPolicy = ({
  itinerary,
  onCancel,
  cancelScenario,
}: CarTripCancellationPolicyProps) => {
  const { t } = useI18nContext();

  const [block, cn] = useModuleBEM(defaultStyles, ComponentName);

  const { breakdown } = useCarCancelPolicyBreakdown(cancelScenario, itinerary);

  const isNonRefundable =
    cancelScenario.GroundCancelScenarioV2 ===
    GroundCancelScenarioV2Enum.NonRefundable;

  const cancelCopy = getCancelCopy(cancelScenario)?.body?.[0];

  const carBreakdown = breakdown
    ? ({ productType: ProductType.CARS, data: breakdown } as const)
    : null;

  return (
    <Card className={block}>
      <section className={cn("title-section")}>
        <h1 className={cn("title")}>{t("selfServe.yourCancellation")}</h1>

        {cancelCopy != null ? (
          <p
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(cancelCopy),
            }}
          />
        ) : null}
      </section>

      {!isNonRefundable && carBreakdown != null && (
        <RefundBreakdown breakdown={carBreakdown} />
      )}

      <CancelImportantInfo
        items={getCancelCopy(cancelScenario)?.importantInfo}
      />

      {!isNonRefundable && (
        <Button onClick={onCancel}>
          {t("core-cars.postbooking.confirmCancel")}
        </Button>
      )}
    </Card>
  );
};
